/**
 * NominationListPage — the nominations this caller may see, and the decisions they may make.
 *
 * Route: `/event/nominations`. From the Figma's "Marketing View - Marketing/BD GM Review"
 * band, which is why one page serves both a marketer and a BD GM.
 *
 * One page for two audiences, because the service already draws the line: `ListNominations`
 * takes no parameters and scopes the result to the caller — a marketer or admin gets every
 * nomination, anyone else gets their own plus those created by their direct and indirect
 * reports, resolved through the manager chain. So a BD sees their submissions, a BD GM sees
 * their team's, and neither needs a different page or a different request.
 *
 * Who is offered Approve / Reject (mayDecideNomination): on a nomination still PENDING_REVIEW,
 * any marketer or admin — the event owner included — and a BD for any nomination they did not
 * submit, which, since a BD's list holds only their own and their reports', is a report's. Nobody
 * decides their own. This mirrors the service's `requireApprover` (a marketer or admin, or a
 * manager of the nomination's creator), so an offered button is one the service accepts.
 *
 * What the status machine allows, which is what the actions follow:
 *
 *   PENDING_REVIEW  → approve or reject
 *   APPROVED        → approving again is a no-op; rejecting is a 409
 *   REJECTED        → rejecting again is a no-op and keeps the first reason; approving is 409
 *
 * So a decision is final, and it emails the creating BD. Rejecting asks for a reason first:
 * it is the one thing the BD will read, and the service never overwrites it.
 *
 * ── Registration Status ────────────────────────────────────────────────────────────────
 *
 * The design lists a Registration Status column. `Nomination` has no such field — an
 * approved nomination becoming a registration is not modeled — so the column is left out
 * rather than filled with a row of dashes. This note is the record of the omission.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  BreadcrumbGroup,
  Button,
  CollectionPreferences,
  ContentLayout,
  FormField,
  Header,
  Link,
  Modal,
  Pagination,
  SegmentedControl,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
  TextFilter,
  type CollectionPreferencesProps,
  type SelectProps,
  type TableProps,
} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { useAuth } from '../../../context/AuthContext';
import {
  eventHubCalendarClient,
  type Event,
  type Nomination,
} from '../../client/EventCalendarClient';
import { useEventList } from '../../hooks/useEventList';
import { useNominationList } from '../../hooks/useNominationList';
import {
  NOMINATION_STATUS_INDICATOR,
  NOMINATION_STATUS_LABELS,
  NOMINATION_STATUS_ORDER,
} from '../../utils/nominationStatus';
import { hasEventHubAccess, mayDecideNomination } from '../../utils/authorization';
import { useEventHubRoles } from '../../hooks/useEventHubRoles';
import { formatRange } from '../../utils/eventDates';
import { loadPreferences, savePreferences } from '../../utils/tablePreferences';
import { ownerOf } from '../../utils/eventOwnership';
import {
  columnLabel,
  columnsFor,
  defaultContentDisplay,
  nominationCellText,
} from '../../utils/nominationColumns';
import {
  ATTENDANCE_CONFIRMED,
  ATTENDANCE_UNCONFIRMED,
  REVIEW_MINE,
  awaitsMyReview,
} from '../../utils/nominationCounts';
import { isAttendanceConfirmed } from '../../utils/attendance';
import { NominationDetail } from '../../components/NominationDetail';
import { NOMINATION_PARAM, nominationHref, withOpenNomination } from '../../utils/nominationLink';
import { useEventHubLoading } from '../../components/EventHubLayout/EventHubLoading';
import { AttendanceToggle } from '../../components/AttendanceToggle';
import { useEventHubLogin } from '../../hooks/useEventHubLogin';
import { canonicalJobTitle } from '../../utils/jobTitles';
import { guestNameOf } from '../../utils/guestName';
import { editHrefFor } from '../../utils/nominationEdit';
import '../../components/EventFilters/EventFilters.css';

/** "Everything" for a filter. A sentinel, so an empty field cannot match a real value. */
const ALL = '__ALL__';
const ALL_OPTION: SelectProps.Option = { value: ALL, label: 'All' };

const STATUS_OPTIONS: SelectProps.Option[] = [
  ALL_OPTION,
  ...NOMINATION_STATUS_ORDER.map((s) => ({
    value: s,
    label: NOMINATION_STATUS_LABELS[s],
  })),
];

/** `?review=` — every row, or only those awaiting this reader's decision. */
const REVIEW_OPTIONS: SelectProps.Option[] = [
  ALL_OPTION,
  { value: REVIEW_MINE, label: 'Only mine to review' },
];

/** `?attendance=` — every row, or those whose attendance is (not) confirmed. */
const ATTENDANCE_OPTIONS: SelectProps.Option[] = [
  ALL_OPTION,
  { value: ATTENDANCE_CONFIRMED, label: 'Confirmed' },
  { value: ATTENDANCE_UNCONFIRMED, label: 'Not confirmed' },
];

/** Which of the Figma's two groupings is shown. */
type ViewMode = 'nomination' | 'event';

const DASH = '—';

/**
 * Column preferences: the shared nomination column list (utils/nominationColumns), so this table
 * and the event detail page's Attendance List show the same columns by the same names. This
 * page has no Registration / Attendance Status (a nomination carries neither) and adds Event
 * Name, Event Owner and Actions.
 */
const COLUMN_OPTIONS = columnsFor('nominations').map(({ id, label }) => ({ id, label }));

/**
 * Namespace for this page's stored preferences, versioned whenever the column set changes: a
 * stored column list from before would otherwise keep the old arrangement, and hide any column
 * it does not name. v2: no Nomination ID, Contact Name first, sticky first / last columns.
 * v3: Attendance Confirmed. v4: the shared column list, its #34 defaults and every field.
 */
const PREFERENCES_PAGE = 'nominationList.v4';

/** What the table shows until someone changes it — the #34 defaults (utils/nominationColumns). */
const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: 20,
  wrapLines: false,
  stripedRows: false,
  contentDensity: 'comfortable',
  /* The nominee and the Approve / Reject actions stay in view while the columns between them
     scroll sideways. */
  stickyColumns: { first: 1, last: 1 },
  contentDisplay: defaultContentDisplay('nominations'),
};

/**
 * What the Actions cells need, passed by context rather than captured in a closure.
 *
 * The column definitions have to keep their identity across renders — `useCollection` tracks
 * the sorted column by reference, so rebuilding them makes sorting silently stop working
 * after one click. But a cell also needs the live `deciding` value to show its spinner, and a
 * memoised cell closure would hold whichever value was current when the memo was built.
 *
 * Context resolves that: the columns are memoised on data alone, and a consumer re-renders
 * when the provider's value changes even though the closure around it is stale.
 */
interface DecideControls {
  /** The nomination id currently being decided, or null. */
  deciding: string | null;
  approve: (nomination: Nomination) => void;
  askReject: (nomination: Nomination) => void;
}

const DecideContext = createContext<DecideControls>({
  deciding: null,
  approve: () => {},
  askReject: () => {},
});

/**
 * Approve / Reject for one row, when `mayDecide` (mayDecideNomination: still PENDING_REVIEW, not
 * the reader's own submission, and a marketer, admin or BD). Decided rows get nothing: the
 * opposite decision is a 409 and the same one is a no-op, so a button there could only mislead.
 *
 * Edit instead, when `editHref` is given (utils/nominationEdit editHrefFor: the reader's own
 * nomination, its event still open) — in any status, since editing sends it back to review. The
 * two never meet on one row: deciding excludes one's own submissions, and editing requires them.
 */
function RowActions({
  nomination,
  mayDecide,
  editHref,
}: {
  nomination: Nomination;
  mayDecide: boolean;
  editHref?: string;
}) {
  const { deciding, approve, askReject } = useContext(DecideContext);
  const navigate = useNavigate();

  if (editHref) {
    return (
      <Button variant="inline-link" onClick={() => navigate(editHref)}>
        Edit
      </Button>
    );
  }

  /* Two different reasons for the same dash, and neither is worth a distinct cell: the row is
     already decided, or this reader may not decide. */
  if (!mayDecide) {
    return <Box color="text-status-inactive">{DASH}</Box>;
  }

  // Any decision in flight locks all of them: the list is re-fetched afterwards, so a second
  // decision started meanwhile would be deciding from a list about to be replaced.
  const busy = deciding !== null;
  const mine = deciding === nomination.nominationId;

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Button variant="inline-link" loading={mine} disabled={busy} onClick={() => approve(nomination)}>
        Approve
      </Button>
      <Button variant="inline-link" disabled={busy} onClick={() => askReject(nomination)}>
        Reject
      </Button>
    </SpaceBetween>
  );
}

/** Nomination status as the Figma draws it: an indicator, not bare text. */
function StatusCell({ status }: { status: Nomination['status'] }) {
  if (!status) return <Box color="text-status-inactive">{DASH}</Box>;
  return (
    <StatusIndicator type={NOMINATION_STATUS_INDICATOR[status]}>
      {NOMINATION_STATUS_LABELS[status]}
    </StatusIndicator>
  );
}

/** One event's nominations, counted by status — the Event View's row. */
interface EventGroup {
  eventId: string;
  eventName: string;
  event: Event | undefined;
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

/** Two logins name the same person: compared trimmed and case-insensitively. */
function sameLogin(a: string | undefined, b: string): boolean {
  return (a ?? '').trim().toLowerCase() === b.trim().toLowerCase();
}

export default function NominationListPage() {
  const navigate = useNavigate();
  const { ldap_groups } = useAuth();
  const mayView = hasEventHubAccess(ldap_groups);

  /* Disabled outright for a caller with no Event Hub role: the early return below decides
     what renders, but by then an unconditional fetch has already gone out. */
  const { nominations, loading, error, reload, replace } = useNominationList(mayView);
  /* Stable, so the memoised columns keep their identity. Remembers the row (see `touched`). */
  const onAttendanceUpdated = useCallback(
    (updated: Nomination) => {
      replace(updated);
      if (updated.nominationId) {
        setTouched((prev) => new Set(prev).add(updated.nominationId!));
      }
    },
    [replace],
  );
  /* The login Event Hub acts as: who may tick Attendance Confirmed is the nomination's creator,
     judged as the viewed user while an admin acts as someone. */
  const { login } = useEventHubLogin();
  const roles = useEventHubRoles();
  useEventHubLoading(loading);
  /* Events come along for the names (a nomination carries only `eventId`, and a table of opaque
     ids is unreadable) and the Event Owner column. A failed fetch is reported rather than
     swallowed, though it no longer costs any action. */
  const { events, error: eventsError } = useEventList(mayView);

  const [view, setView] = useState<ViewMode>('nomination');
  const [search, setSearch] = useState('');
  /**
   * Seeded from `?status=…` so the landing page's "Nominations to Review" count opens the
   * rows it counted rather than everything.
   *
   * Read once, at mount, rather than kept in sync with the URL: the filters here are
   * transient working state, and writing every dropdown change back would put a history
   * entry between the reviewer and the page they came from. The links into this page are
   * from other routes, so a mount always happens. An unrecognised value falls through to
   * ALL — the Select would otherwise show a filter nothing matches.
   */
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(() => {
    const wanted = params.get('status');
    return wanted && (NOMINATION_STATUS_ORDER as string[]).includes(wanted) ? wanted : ALL;
  });
  /* Seeded the same way, from `?eventId=` — the event detail page's Attendance List sends its
     Review button here, and arriving at every event's nominations would lose the context the
     reviewer came from. Not validated against the event list, which has not loaded yet at this
     point; an id matching nothing simply shows no rows, which is the honest answer. */
  const [eventId, setEventId] = useState(() => params.get('eventId') || ALL);
  /* Seeded the same way, from `?createdBy=` — the BD home's "My Nominations in Review" links
     here with the BD's own login, so the list shows exactly what it counted. */
  const [creator, setCreator] = useState(() => params.get('createdBy') || ALL);
  /* `?review=mine`: only what awaits this reader's decision (awaitsMyReview) — what the home
     page's "Nominations Awaiting My Review" counts and links here with. */
  const [review, setReview] = useState(() =>
    params.get('review') === REVIEW_MINE ? REVIEW_MINE : ALL,
  );
  /* `?attendance=confirmed|unconfirmed` — the BD home's "My Nominations to Confirm Attendance"
     links here with `unconfirmed`, to the rows whose box is still to be ticked. */
  const [attendance, setAttendance] = useState(() => {
    const wanted = params.get('attendance');
    return wanted === ATTENDANCE_CONFIRMED || wanted === ATTENDANCE_UNCONFIRMED ? wanted : ALL;
  });
  /* Rows whose attendance was ticked or unticked here. They stay in view under an attendance
     filter they no longer match: a box that vanishes the moment it is ticked reads as a fault
     and leaves nothing to untick. Forgotten when that filter changes. */
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const onAttendanceFilter = (value: string) => {
    setAttendance(value);
    setTouched(new Set());
  };

  /**
   * The nomination whose detail modal is open — held in the URL (`?nominationId=`), not in
   * state, so the modal is a link that can be shared (see utils/nominationLink). Unlike the
   * filters above it is read live: opening and closing write it back.
   */
  const openNominationId = params.get(NOMINATION_PARAM);
  /* Replace, not push: opening a nomination is not a page of its own, and Back should leave
     the list rather than step through every nomination looked at. */
  const setOpenRef = useRef<(id: string | null) => void>(() => {});
  setOpenRef.current = (id) => setParams((p) => withOpenNomination(p, id), { replace: true });
  /* Stable, so the memoised columns can call it; the ref keeps it pointing at the current
     setter. */
  const openNomination = useCallback((id: string | null) => setOpenRef.current(id), []);

  /** The nomination being rejected, and the reason being typed for it. */
  const [rejecting, setRejecting] = useState<Nomination | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [deciding, setDeciding] = useState<string | null>(null);
  const [decideError, setDecideError] = useState<string | null>(null);
  /* Seeded from localStorage, so a reviewer who works through nominations daily configures
     page size and columns once. The initialiser is lazy — reading storage on every render
     would be wasted work, and the value cannot change under us. */
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>(
    () => loadPreferences(PREFERENCES_PAGE, DEFAULT_PREFERENCES),
  );
  const pageSize = preferences.pageSize ?? 20;

  function onPreferencesChange(next: CollectionPreferencesProps.Preferences) {
    setPreferences(next);
    savePreferences(PREFERENCES_PAGE, next);
  }

  const eventsById = useMemo(() => {
    const map = new Map<string, Event>();
    for (const e of events) if (e.eventId) map.set(e.eventId, e);
    return map;
  }, [events]);

  /** An event's name, falling back to its id — never blank, so a row is always identifiable. */
  const nameOf = useMemo(
    () => (id: string | undefined) =>
      (id && eventsById.get(id)?.eventName) || id || DASH,
    [eventsById],
  );


  const openedNomination = useMemo(
    () =>
      openNominationId
        ? nominations.find((n) => n.nominationId === openNominationId)
        : undefined,
    [nominations, openNominationId],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return nominations.filter((n) => {
      if (status !== ALL && n.status !== status) return false;
      if (eventId !== ALL && n.eventId !== eventId) return false;
      /* Trimmed and case-insensitive, as countOwnAwaitingReview counts: the BD home's Pending
         Review link passes the JWT alias, and createdBy is whatever the service recorded. */
      if (creator !== ALL && !sameLogin(n.createdBy, creator)) return false;
      if (review === REVIEW_MINE && !awaitsMyReview(n, login, roles)) {
        return false;
      }
      if (attendance !== ALL && !touched.has(n.nominationId ?? '')) {
        if (isAttendanceConfirmed(n) !== (attendance === ATTENDANCE_CONFIRMED)) return false;
      }
      if (!needle) return true;
      /* Searches what the table shows, so a hit is always visible in a row. The reject
         reason is included: "which ones did we turn down for capacity" is a real question. */
      return [
        n.nominationId,
        nameOf(n.eventId),
        guestNameOf(n.guestInformation),
        canonicalJobTitle(n.guestInformation?.jobTitle),
        n.accountInformation?.companyName,
        n.bdName,
        n.createdBy,
        n.rejectReason,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [nominations, search, status, eventId, creator, review, attendance, touched, roles, login, nameOf]);

  /** The filtered set rolled up per event, for the Event View. */
  const groups = useMemo<EventGroup[]>(() => {
    const byEvent = new Map<string, EventGroup>();
    for (const n of filtered) {
      const id = n.eventId ?? '';
      let group = byEvent.get(id);
      if (!group) {
        group = {
          eventId: id,
          eventName: nameOf(id),
          event: id ? eventsById.get(id) : undefined,
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0,
        };
        byEvent.set(id, group);
      }
      group.total += 1;
      if (n.status === 'PENDING_REVIEW') group.pending += 1;
      else if (n.status === 'APPROVED') group.approved += 1;
      else if (n.status === 'REJECTED') group.rejected += 1;
    }
    // Most pending first: the point of this view is where the reviewing is owed.
    return [...byEvent.values()].sort(
      (a, b) => b.pending - a.pending || a.eventName.localeCompare(b.eventName),
    );
  }, [filtered, nameOf, eventsById]);

  /**
   * Records one decision.
   *
   * Re-fetches rather than patching the row: the decision also stamps decidedBy/decidedAt,
   * and a 409 means someone else already decided — in which case the list was stale and
   * patching it would write a state the service does not hold.
   */
  async function decide(nomination: Nomination, approve: boolean, reason?: string) {
    if (!nomination.nominationId) return;
    setDeciding(nomination.nominationId);
    setDecideError(null);
    setRejecting(null);
    try {
      const client = eventHubCalendarClient();
      if (approve) await client.approveNomination(nomination.nominationId);
      else await client.rejectNomination(nomination.nominationId, reason);
      reload();
    } catch (e: unknown) {
      setDecideError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeciding(null);
    }
  }

  const controls = useMemo<DecideControls>(
    () => ({
      deciding,
      approve: (n) => decide(n, true),
      askReject: (n) => {
        setRejectReason('');
        setRejecting(n);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `decide` is re-created each
    // render by design; only `deciding` changes what a consumer shows.
    [deciding],
  );

  /**
   * Memoised on the data alone — see DecideControls. The columns follow the Figma's order;
   * Registration Status is absent because the model has no such field.
   */
  const columnDefinitions = useMemo<TableProps.ColumnDefinition<Nomination>[]>(
    () => [
      /* The nominee first, and the way into the detail modal: who was nominated is what a
         reader scans for, where the opaque Nomination ID it replaces was not. A real link —
         new tab and copy work — that opens in place on a plain click. */
      {
        id: 'guest',
        header: columnLabel('guest'),
        minWidth: 160,
        sortingComparator: (a, b) =>
          (guestNameOf(a.guestInformation) ?? '').localeCompare(
            guestNameOf(b.guestInformation) ?? '',
          ),
        cell: (n) => {
          const id = n.nominationId;
          const name = guestNameOf(n.guestInformation) || '(unnamed)';
          if (!id) return name;
          return (
            <Link
              href={nominationHref(id, n.eventId)}
              onFollow={(e) => {
                e.preventDefault();
                openNomination(id);
              }}
            >
              {name}
            </Link>
          );
        },
      },
      {
        id: 'eventName',
        header: 'Event Name',
        minWidth: 180,
        sortingComparator: (a, b) => nameOf(a.eventId).localeCompare(nameOf(b.eventId)),
        // A real href, so a row can be opened in a new tab, but the router handles the
        // ordinary click rather than reloading the app.
        cell: (n) =>
          n.eventId ? (
            <Link
              href={`/event/events/${n.eventId}`}
              onFollow={(ev) => {
                ev.preventDefault();
                navigate(`/event/events/${n.eventId}`);
              }}
            >
              {nameOf(n.eventId)}
            </Link>
          ) : (
            DASH
          ),
      },
      {
        id: 'company',
        header: columnLabel('company'),
        sortingComparator: (a, b) =>
          (a.accountInformation?.companyName ?? '').localeCompare(
            b.accountInformation?.companyName ?? '',
          ),
        cell: (n) => n.accountInformation?.companyName ?? DASH,
      },
      {
        id: 'jobTitle',
        header: 'Job Title',
        // The English key, as the Attendance List shows it.
        cell: (n) => canonicalJobTitle(n.guestInformation?.jobTitle) ?? DASH,
      },
      {
        id: 'bdName',
        header: 'BD Name',
        // Falls back to the login: bdName is resolved from the GCR permission service and
        // can come back empty, while createdBy is always the BD who submitted it.
        cell: (n) => n.bdName || n.createdBy || DASH,
      },
      {
        id: 'status',
        header: 'Nomination Status',
        sortingComparator: (a, b) =>
          NOMINATION_STATUS_ORDER.indexOf(a.status ?? 'PENDING_REVIEW') -
          NOMINATION_STATUS_ORDER.indexOf(b.status ?? 'PENDING_REVIEW'),
        cell: (n) => <StatusCell status={n.status} />,
      },
      {
        id: 'phone',
        header: columnLabel('phone'),
        cell: (n) => n.guestInformation?.phoneNumber || DASH,
      },
      {
        id: 'email',
        header: columnLabel('email'),
        cell: (n) => n.guestInformation?.email || DASH,
      },
      {
        id: 'jobRole',
        header: columnLabel('jobRole'),
        cell: (n) => canonicalJobTitle(n.guestInformation?.jobRole) || DASH,
      },
      // Read straight off the nomination — the same text the Attendance List shows.
      ...(
        ['sfdcAccountId', 'accountTags', 'customerBu', 'bdPhone', 'bdBu', 'reviewedBy', 'comments'] as const
      ).map(
        (id): TableProps.ColumnDefinition<Nomination> => ({
          id,
          header: columnLabel(id),
          minWidth: id === 'comments' ? 200 : undefined,
          cell: (n) => nominationCellText(id, n) || DASH,
        }),
      ),
      {
        id: 'eventOwner',
        header: 'Event Owner',
        // The same owner the Approve / Reject gate turns on, so "why can I not decide this"
        // has an answer the reader can switch on.
        cell: (n) => ownerOf(eventsById.get(n.eventId ?? '')) || DASH,
      },
      {
        id: 'submittedOn',
        header: columnLabel('submittedOn'),
        sortingComparator: (a, b) =>
          (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0),
        cell: (n) => nominationCellText('submittedOn', n) || DASH,
      },
      {
        id: 'confirmed',
        header: columnLabel('confirmed'),
        minWidth: 170,
        // The nominating BD ticks it on an approved nomination; everyone else reads it.
        cell: (n: Nomination) => (
          <AttendanceToggle nomination={n} login={login} onUpdated={onAttendanceUpdated} />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        minWidth: 180,
        cell: (n: Nomination) => (
          <RowActions
            nomination={n}
            mayDecide={mayDecideNomination(n, login, roles)}
            editHref={editHrefFor(n, n.eventId ? eventsById.get(n.eventId) : undefined, login, new Date())}
          />
        ),
      },
    ],
    [nameOf, navigate, eventsById, openNomination, login, roles, onAttendanceUpdated],
  );

  /** The Event View's columns. Counts, because that is what a roll-up can honestly show. */
  const groupColumnDefinitions = useMemo<TableProps.ColumnDefinition<EventGroup>[]>(
    () => [
      {
        id: 'eventName',
        header: 'Event Name',
        minWidth: 200,
        sortingComparator: (a, b) => a.eventName.localeCompare(b.eventName),
        cell: (g) =>
          g.eventId ? (
            <Link
              href={`/event/events/${g.eventId}`}
              onFollow={(ev) => {
                ev.preventDefault();
                navigate(`/event/events/${g.eventId}`);
              }}
            >
              {g.eventName}
            </Link>
          ) : (
            g.eventName
          ),
      },
      {
        id: 'when',
        header: 'Event Date',
        cell: (g) => (g.event ? formatRange(g.event.startDate, g.event.endDate) : DASH),
      },
      {
        id: 'pending',
        header: 'Pending Review',
        sortingComparator: (a, b) => a.pending - b.pending,
        cell: (g) =>
          g.pending ? (
            <StatusIndicator type="pending">{g.pending}</StatusIndicator>
          ) : (
            <Box color="text-status-inactive">0</Box>
          ),
      },
      { id: 'approved', header: 'Approved', cell: (g) => g.approved },
      { id: 'rejected', header: 'Rejected', cell: (g) => g.rejected },
      { id: 'total', header: 'Total', cell: (g) => g.total },
      {
        id: 'actions',
        header: 'Actions',
        minWidth: 150,
        // Hands off to the nomination view, filtered — this table cannot decide anything,
        // since a decision is per nomination.
        cell: (g) => (
          <Button
            variant="inline-link"
            onClick={() => {
              setEventId(g.eventId);
              setView('nomination');
            }}
          >
            Review
          </Button>
        ),
      },
    ],
    [navigate],
  );

  const { items, collectionProps, paginationProps } = useCollection(filtered, {
    pagination: { pageSize },
    sorting: {},
  });

  const groupCollection = useCollection(groups, {
    pagination: { pageSize },
    sorting: {},
  });

  const eventFilterOptions = useMemo<SelectProps.Option[]>(() => {
    /* Only events that actually have a nomination in the visible set. Listing every event
       would offer filters that can only ever empty the table. */
    const ids = new Set<string>();
    for (const n of nominations) if (n.eventId) ids.add(n.eventId);
    return [
      ALL_OPTION,
      ...[...ids]
        .map((id) => ({ value: id, label: nameOf(id) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [nominations, nameOf]);

  const creatorOptions = useMemo<SelectProps.Option[]>(() => {
    const set = new Set<string>();
    for (const n of nominations) if (n.createdBy) set.add(n.createdBy);
    return [ALL_OPTION, ...[...set].sort().map((v) => ({ value: v, label: v }))];
  }, [nominations]);

  const selected = (options: SelectProps.Option[], value: string) =>
    options.find((o) => o.value === value) ?? ALL_OPTION;

  const header = (
    <>
      <BreadcrumbGroup
        items={[
          { text: 'Event Hub', href: '/event' },
          { text: 'Nomination List', href: '' },
        ]}
        onFollow={(e) => {
          e.preventDefault();
          if (e.detail.href) navigate(e.detail.href);
        }}
      />
      <Header variant="h1">Nomination List</Header>
    </>
  );

  if (!mayView) {
    return (
      <ContentLayout header={header}>
        <Alert type="error" header="You cannot view nominations">
          Viewing nominations requires an Event Hub role.
        </Alert>
      </ContentLayout>
    );
  }

  if (error) {
    return (
      <ContentLayout header={header}>
        <Alert
          type="error"
          header="Failed to load nominations"
          action={<Button onClick={reload}>Retry</Button>}
        >
          {error}
        </Alert>
      </ContentLayout>
    );
  }

  /* Laid out like the Event Calendar's filter bar (EventFilters): each control has its label
     above it, visible rather than aria-only, so an "All" dropdown says what it filters. */
  const filterControls = (
    <div className="eh-filters eh-filters--in-table">
      <FormField label="Search">
        <TextFilter
          filteringText={search}
          onChange={({ detail }) => setSearch(detail.filteringText)}
          filteringPlaceholder="Find resources"
          filteringAriaLabel="Find nominations"
        />
      </FormField>
      <FormField label="Nomination Status">
        <Select
          selectedOption={selected(STATUS_OPTIONS, status)}
          options={STATUS_OPTIONS}
          onChange={({ detail }) => setStatus(String(detail.selectedOption.value))}
        />
      </FormField>
      <FormField label="Event Name">
        <Select
          selectedOption={selected(eventFilterOptions, eventId)}
          options={eventFilterOptions}
          onChange={({ detail }) => setEventId(String(detail.selectedOption.value))}
          filteringType="auto"
        />
      </FormField>
      {/* The nomination's createdBy — the BD who submitted it, which the BD home's Pending Review
          link pre-selects — not the event's creator. */}
      <FormField label="Nominated By">
        <Select
          selectedOption={selected(creatorOptions, creator)}
          options={creatorOptions}
          onChange={({ detail }) => setCreator(String(detail.selectedOption.value))}
          filteringType="auto"
        />
      </FormField>
      <FormField label="Attendance Confirmed">
        <Select
          selectedOption={selected(ATTENDANCE_OPTIONS, attendance)}
          options={ATTENDANCE_OPTIONS}
          onChange={({ detail }) => onAttendanceFilter(String(detail.selectedOption.value))}
        />
      </FormField>
      <FormField label="Awaiting My Review">
        <Select
          selectedOption={selected(REVIEW_OPTIONS, review)}
          options={REVIEW_OPTIONS}
          onChange={({ detail }) => setReview(String(detail.selectedOption.value))}
        />
      </FormField>
    </div>
  );

  const tableHeader = (
    <Header
      variant="h2"
      counter={loading ? undefined : `(${filtered.length}/${nominations.length})`}
      // The Figma's two-way toggle. Both are built: the nomination view is the table the
      // design draws, and the event view rolls the same rows up per event.
      actions={
        <SegmentedControl
          selectedId={view}
          onChange={({ detail }) => setView(detail.selectedId as ViewMode)}
          label="Grouping"
          options={[
            { id: 'event', text: 'Event View' },
            { id: 'nomination', text: 'Nomination View' },
          ]}
        />
      }
    >
      Nominations
    </Header>
  );

  const emptyState = (
    <Box textAlign="center" padding={{ vertical: 'l' }}>
      <Box variant="strong" color="text-body-secondary">
        {nominations.length === 0 ? 'No nominations' : 'No matching nominations'}
      </Box>
      <Box color="text-status-inactive" fontSize="body-s" padding={{ top: 'xxs' }}>
        {nominations.length === 0
          ? 'Nominations submitted by BD appear here.'
          : 'Try clearing a filter.'}
      </Box>
    </Box>
  );

  /* The Figma's Preferences modal. Cloudscape's CollectionPreferences is that modal — page
     size, wrap lines, striped rows, compact mode, sticky columns and the column list — so this
     is configuration rather than a rebuild. Sticky columns are offered because the design
     offers them, and they earn their place once the hidden columns are switched on. */
  const tablePreferences = (
    <CollectionPreferences
      preferences={preferences}
      onConfirm={({ detail }) => onPreferencesChange(detail)}
      title="Preferences"
      confirmLabel="Confirm"
      cancelLabel="Cancel"
      pageSizePreference={{
        title: 'Page size',
        options: [
          { value: 10, label: '10 resources' },
          { value: 20, label: '20 resources' },
        ],
      }}
      wrapLinesPreference={{
        label: 'Wrap lines',
        description: 'Select to see all the text and wrap the lines.',
      }}
      stripedRowsPreference={{
        label: 'Striped rows',
        description: 'Select to add alternating shaded rows.',
      }}
      contentDensityPreference={{
        label: 'Compact mode',
        description: 'Select to display content in a denser, more compact mode.',
      }}
      stickyColumnsPreference={{
        firstColumns: {
          title: 'Stick first column(s)',
          description: 'Keep the first column(s) visible while horizontally scrolling the table.',
          options: [
            { label: 'None', value: 0 },
            { label: 'First column', value: 1 },
            { label: 'Second column', value: 2 },
          ],
        },
        lastColumns: {
          title: 'Stick last column',
          description: 'Keep the last column visible while horizontally scrolling the table.',
          options: [
            { label: 'None', value: 0 },
            { label: 'Last column', value: 1 },
          ],
        },
      }}
      contentDisplayPreference={{
        title: 'Column preferences',
        description: 'Customize the columns visibility and order.',
        options: COLUMN_OPTIONS.map((c) => ({
          id: c.id,
          label: c.label,
          /* Contact Name always shows: it is the way into the detail modal, and a reader who
             hid it would have no route back to it. */
          alwaysVisible: c.id === 'guest',
        })),
      }}
    />
  );

  const paginationLabels = {
    nextPageLabel: 'Next page',
    previousPageLabel: 'Previous page',
    pageLabel: (n: number) => `Page ${n}`,
  };

  return (
    <ContentLayout header={header}>
      <SpaceBetween size="l">
        {/* Not fatal — the table still reads, with ids where names would be. */}
        {eventsError ? (
          <Alert type="warning" header="Could not load the events">
            {eventsError} — event names are unavailable until this loads.
          </Alert>
        ) : null}

        {decideError ? (
          <Alert
            type="error"
            dismissible
            onDismiss={() => setDecideError(null)}
            header="Could not record that decision"
          >
            {decideError}
          </Alert>
        ) : null}

        {/* Found in the whole set, not the filtered rows: a shared link must open whatever the
            filters are. Not found once loaded means the caller cannot see it, or it does not
            exist — the modal says so rather than silently showing nothing. */}
        {openNominationId ? (
          <NominationDetail
            nomination={openedNomination}
            loading={loading}
            error={error}
            eventName={nameOf(openedNomination?.eventId)}
            onDismiss={() => openNomination(null)}
            editHref={
              openedNomination
                ? editHrefFor(
                    openedNomination,
                    openedNomination.eventId ? eventsById.get(openedNomination.eventId) : undefined,
                    login,
                    new Date(),
                  )
                : undefined
            }
          />
        ) : null}

        {rejecting ? (
          <Modal
            visible
            /* The design's title (`245:29118`). Not a question — the modal is reached from a
               Reject button, so what is being decided is already established; what it asks for
               is the reason. */
            header="Review Nomination"
            onDismiss={() => setRejecting(null)}
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setRejecting(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() =>
                      decide(rejecting, false, rejectReason.trim() || undefined)
                    }
                  >
                    Confirm
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="s">
              <Box>
                {guestNameOf(rejecting.guestInformation) || 'This guest'} will not attend{' '}
                {nameOf(rejecting.eventId)}.
              </Box>
              <FormField
                // The design's label, optional marker and all.
                label="Rejection Reason - Optional"
              >
                {/* No placeholder: the FormField label above already says what this is and
                    that it is optional, and the Figma's own "This is a placeholder" is mock
                    text rather than copy. */}
                <Textarea
                  value={rejectReason}
                  onChange={({ detail }) => setRejectReason(detail.value)}
                  ariaLabel="Rejection Reason"
                />
              </FormField>
              {/* The two things worth knowing before committing: it is sent, and it sticks.
                  The service never overwrites a reject reason. */}
              <Alert type="info">
                The reason is emailed to {rejecting.bdName || rejecting.createdBy || 'the BD'}{' '}
                and cannot be changed afterwards.
              </Alert>
            </SpaceBetween>
          </Modal>
        ) : null}

        <DecideContext.Provider value={controls}>
          {view === 'nomination' ? (
            <Table
              {...collectionProps}
              items={items}
              columnDefinitions={columnDefinitions}
              columnDisplay={preferences.contentDisplay}
              wrapLines={preferences.wrapLines}
              stripedRows={preferences.stripedRows}
              contentDensity={preferences.contentDensity}
              stickyColumns={{
                first: preferences.stickyColumns?.first ?? 0,
                last: preferences.stickyColumns?.last ?? 0,
              }}
              preferences={tablePreferences}
              trackBy={(n) => n.nominationId ?? ''}
              loading={loading}
              loadingText="Loading nominations"
              variant="container"
              header={tableHeader}
              filter={filterControls}
              empty={emptyState}
              pagination={
                filtered.length > pageSize ? (
                  <Pagination {...paginationProps} ariaLabels={paginationLabels} />
                ) : undefined
              }
            />
          ) : (
            <Table
              {...groupCollection.collectionProps}
              items={groupCollection.items}
              columnDefinitions={groupColumnDefinitions}
              trackBy={(g) => g.eventId}
              loading={loading}
              loadingText="Loading nominations"
              variant="container"
              header={tableHeader}
              filter={filterControls}
              empty={emptyState}
              pagination={
                groups.length > pageSize ? (
                  <Pagination
                    {...groupCollection.paginationProps}
                    ariaLabels={paginationLabels}
                  />
                ) : undefined
              }
            />
          )}
        </DecideContext.Provider>
      </SpaceBetween>
    </ContentLayout>
  );
}
